import React, { FC, useEffect, useState } from 'react';
import Loader from '../../components/Loader';
import ErrorResponse from '../../components/ErrorResponse';
import SimpleHeader from '../../components/layout/SimpleHeader';
import { useTranslation } from 'react-i18next';
import { Empty, Table, TableColumnsType } from 'antd';
import Button from '../../components/Button';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import DownloadButton from '../../components/DownloadButton';
import Pagination from '../../components/Pagination';
import ExtendButton from '../../components/ExtendButton';
import styled from 'styled-components';
import SelectLevel from '../../components/SelectLevel';
import {
  useExcelBatchUploadMutation,
  useExcelCampusBookQuantityBatchUploadMutation,
  useLazyGetBooksBySearchDataQuery,
  useLazyGetExcelByBooksQuery,
  useSaveUnitBookMutation,
  useUpdateBasicBookInformationMutation,
} from '../../api/books/bookManagement.api';
import BookFileUploadModal from '../../components/modals/BookFileUploadModal';
import {
  infoVariant,
  successVariant,
  warningVariant,
} from '../../lib/barVariant';
import { useSnackbar } from 'notistack';
import ScrollView from '../../components/payment/ScrollView';
import BookBasicInformation from './bookManagement/BookBasicInformation';
import BookCopyInformation from './bookManagement/BookCopyInformation';
import SelectCampus from '../../components/SelectCampus';
import { campusHeadOffice } from '../../lib/data';
import { getCommonCodeLabel } from '../../lib/store';
import moment from 'moment';
import exportTable from '../../lib/exportTable';
import useBarcodeScanner from '../../hooks/useBarcodeScanner';
import Split from 'react-split';
import TableEmpty from '../../components/common/TableEmpty';

const Block = styled.div`
  .close-left {
    transform: translate(-8.3333%);
  }

  .table-inside {
    width: 100%;

    th {
      background: ghostwhite;
    }
  }

  .img-preview {
    max-width: 150px;
  }

  .dummyDiv {
    height: 30px;
  }
`;

const Books: FC = () => {
  const { t, i18n } = useTranslation();
  const { currentCampus: campusCode } = useSelector(
    (state: RootState) => state.auth,
  );
  const BOOK_SAVE_VALIDATION_KEY = [
    'bookName',
    'author',
    'isbn',
    'publisherName',
    'courseCode',
    'bookDiv',
  ];
  const isHeadOffice = (campusCode: string) => campusCode === campusHeadOffice;
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const [isExpand, setIsExpand] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const barcode = useBarcodeScanner();

  const [isAllFileVisible, setIsAllFileVisible] = useState<boolean>(false);
  const [isBookBatchQuantityRegistration, setIsBookBatchQuantityRegistration] =
    useState<boolean>(false);
  const [isModify, setIsModify] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [file, setFile] = useState<any>(null);

  const [page, setPage] = useState(1);
  const [searchData, setSearchData] = useState({
    clientCode: campusCode,
    bookName: undefined,
    author: undefined,
    isbn: '',
    publisherName: undefined,
    courseCode: '',
    bookLevel: undefined,
    pageSize: 50,
    pageNumber: page,
  });

  const [
    excelBatchUpload,
    {
      data: batchSaveData,
      isSuccess: isBatchUploadSuccess,
      isLoading: isUploading,
    },
  ] = useExcelBatchUploadMutation();

  const [
    excelBatchQuantityUpload,
    {
      data: bookQuantityBatchSaveData,
      isSuccess: isBookQuantityBatchSuccess,
      isLoading: isQuantityUploading,
    },
  ] = useExcelCampusBookQuantityBatchUploadMutation();

  const [
    trigger,
    { data: response, isFetching, isLoading: isBooksLoading, error: bookError },
  ] = useLazyGetBooksBySearchDataQuery();

  const [excelBooksTrigger] = useLazyGetExcelByBooksQuery();
  const [patchBookTrigger, { data: updateData, isSuccess: isPatchSuccess }] =
    useUpdateBasicBookInformationMutation();
  const [saveUnitBook, { data: saveData, isSuccess: isBookSaveSuccess }] =
    useSaveUnitBookMutation();
  const [information, setInformation] = useState<any>(null);
  const [campusBookCopyInformation, setCampusBookCopyInformation] =
    useState<any>(null);

  const columns: TableColumnsType<any> = [
    {
      title: t('booksManagement.books.column1'),
      dataIndex: 'id',
      width: 50,
      align: 'center',
      render: (item, record, index) => (
        <>{(page - 1) * searchData.pageSize + index + 1}</>
      ),
    },
    {
      title: t('booksManagement.books.column2'),
      dataIndex: 'bookName',
      align: 'center',
      ellipsis: true,
      sorter: (a, b) => a.bookName.localeCompare(b.bookName),
      render: value => {
        return (<div className='text-left ant-table-cell-ellipsis'>{value}</div>)
      }
    },
    {
      title: t('booksManagement.books.column3'),
      dataIndex: 'author',
      align: 'center',
      sorter: (a, b) => a.author.localeCompare(b.author),
    },
    {
      title: t('booksManagement.books.column4'),
      dataIndex: 'isbn',
      key: 'isbn',
      align: 'center',
      width: 150,
      sorter: (a, b) => a.isbn.localeCompare(b.isbn),
    },
    {
      title: t('booksManagement.books.column5'),
      dataIndex: 'publisherName',
      align: 'center',
      sorter: (a, b) => a.publisherName.localeCompare(b.publisherName),
    },
  ];

  const onExportTable = async () => {
    const response = await excelBooksTrigger({ ...searchData });
    const _books = response?.data?.data;

    let excelData = _books?.bookDtos.map((book: any) => {
      let sum = 0;
      for (let key in _books?.countCampusMaps) {
        const findCampusInfo = _books?.countCampusMaps[key].find(
          (item: any) => item?.bookNo === book?.bookNo,
        );
        book = {
          ...book,
          [findCampusInfo?.campusCode]: findCampusInfo?.bookCount,
        };
        sum += findCampusInfo?.bookCount ? findCampusInfo?.bookCount : 0;
      }

      return {
        ...book,
        genreName: getCommonCodeLabel('BK0001', book!.genreDiv, i18n.language),
        totalCount: sum,
      };
    });

    let excelHeader = [
      {
        title: t('booksManagement.books.column1'),
        dataIndex: 'bookNo',
        key: 'bookNo',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column2'),
        dataIndex: 'bookName',
        key: 'bookName',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column3'),
        dataIndex: 'author',
        key: 'author',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column4'),
        dataIndex: 'isbn',
        key: 'isbn',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column5'),
        dataIndex: 'publisherName',
        key: 'publisherName',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column6'),
        dataIndex: 'publicationYear',
        key: 'publisherName',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column7'),
        dataIndex: 'seriesName',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column8'),
        dataIndex: 'genreName',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column9'),
        dataIndex: 'bookDiv',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column10'),
        dataIndex: 'theme',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column11'),
        dataIndex: 'courseName',
        align: 'center',
      },
      {
        title: t('booksManagement.books.column12'),
        dataIndex: 'bookLevel',
        align: 'center',
      },
    ];
    let campusHeader = campusData
      ?.map((item) => {
        return {
          title: isHeadOffice(campusCode) ? item?.campusKorName : t('booksManagement.books.column43'),
          dataIndex: item?.campusCode,
          align: 'center',
        };
      })
      .filter((header: any) => {
        if (isHeadOffice(campusCode)) {
          return !isHeadOffice(header.dataIndex);
        }
        return header.dataIndex === campusCode;
      });

    const excelColumns = [
      ...excelHeader,
      ...campusHeader,
      isHeadOffice(campusCode)
        ? {
            title: t('booksManagement.books.column13'),
            dataIndex: 'totalCount',
            align: 'center',
          }
        : {},
    ];

    const currentDate = moment(new Date());
    exportTable(
      excelColumns,
      excelData,
      `${t('books.text.5')}_(${currentDate.format('YYYYMMDD')})`,
      t('books.text.5'),
      [10, 30, 20, 20, 20, 10, 10, 10, 10, 10, 10],
    );
  };

  const handleExcelBatchUploadApply = () => {
    if (!file) {
      enqueueSnackbar(t('booksManagement.books.message1'), warningVariant);
      return;
    }
    let formData = new FormData();
    formData.append('excel', file);
    excelBatchUpload(formData);
  };

  const handleExcelBatchQuantityUploadApply = () => {
    if (!file) {
      enqueueSnackbar(t('booksManagement.books.message1'), warningVariant);
      return;
    }
    let formData = new FormData();
    formData.append('excel', file);
    formData.append('campusCode', campusHeadOffice);
    excelBatchQuantityUpload(formData);
  };

  const handleExcelBatchUploadCancel = () => {
    setIsBookBatchQuantityRegistration(false);
    setIsAllFileVisible(false);
    setFile(null);
  };

  const handleModifyForm = () => {
    if (!isModify) {
      setIsModify(true);
      return;
    }

    if (!information) {
      enqueueSnackbar(t('booksManagement.books.message2'), infoVariant);
      return;
    }

    const emptyValues = BOOK_SAVE_VALIDATION_KEY.filter(
      (key: string) => !information[key],
    );

    if (emptyValues.length) {
      enqueueSnackbar(t('booksManagement.books.message2'), infoVariant);
      return;
    }

    if (information?.isbn.length < 10 || 13 < information?.isbn.length) {
      enqueueSnackbar(t('booksManagement.books.message3'), infoVariant);
      return;
    }

    if (!information?.bookNo) {
      const data = {
        bookDto: information,
        bookDuplicateCampusDtos: campusBookCopyInformation,
      };

      saveUnitBook(data);
      return;
    }

    patchBookTrigger(information);
  };

  useEffect(() => {
    if (page) {
      trigger({ ...searchData, pageNumber: page });
    }
  }, [page]);

  useEffect(() => {
    setSearchData({ ...searchData, clientCode: campusCode });
    setSelectedRow(null);
    setPage(1);
    trigger({ ...searchData, clientCode: campusCode });
  }, [campusCode]);

  useEffect(() => {
    if (!isPatchSuccess) return;

    if (updateData.result === 'fail') {
      enqueueSnackbar(t(updateData.data), warningVariant);
      return;
    }

    setIsModify(false);
    enqueueSnackbar(t('booksManagement.books.message4'), successVariant);
  }, [isPatchSuccess]);

  useEffect(() => {
    if (!isBookSaveSuccess) return;

    if (saveData.result === 'fail') {
      enqueueSnackbar(t(saveData.data), warningVariant);
      return;
    }

    setIsModify(false);
    enqueueSnackbar(t('booksManagement.books.message5'), successVariant);
    const findSaveData = response?.data?.books?.find(
      (book: any) => book?.isbn === selectedRow?.isbn,
    );
    setSelectedRow(findSaveData);
  }, [isBookSaveSuccess]);

  useEffect(() => {
    if (!isBatchUploadSuccess) return;

    setIsAllFileVisible(false);

    if (batchSaveData.result === 'fail') {
      enqueueSnackbar(t(batchSaveData.data), warningVariant);
      return;
    }

    enqueueSnackbar(t('booksManagement.books.message5'), successVariant);
  }, [isBatchUploadSuccess]);

  useEffect(() => {
    if (!isBookQuantityBatchSuccess) return;

    setIsBookBatchQuantityRegistration(false);

    if (bookQuantityBatchSaveData.result === 'fail') {
      enqueueSnackbar(t(bookQuantityBatchSaveData.data), warningVariant);
      return;
    }

    enqueueSnackbar(t('booksManagement.books.message5'), successVariant);
  }, [isBookQuantityBatchSuccess]);

  useEffect(() => {
    if (barcode !== null) {
      setSearchData({ ...searchData, isbn: barcode });
      trigger({ ...searchData, isbn: barcode });
    }
  }, [barcode]);

  return (
    <>
    <SimpleHeader
        iconFile="icon-book-big.png"
        parentMenu={t('common.menu.book')}
        currentMenu={t('booksManagement.main.title')}
        onlyTitle={true}
      />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('booksManagement.books.column2')}</label>
            <div>
              <input
                type="text"
                placeholder={t('booksManagement.books.column2')}
                className="form-control"
                name="bookName"
                onChange={(e) =>
                  setSearchData({
                    ...searchData,
                    [e.target.name]: e.target.value,
                  })
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter')
                    trigger({ ...searchData, pageNumber: 1 });
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('booksManagement.books.column3')}</label>
            <div>
              <input
                type="text"
                placeholder={t('booksManagement.books.column3')}
                className="form-control"
                name="author"
                onChange={(e) =>
                  setSearchData({
                    ...searchData,
                    [e.target.name]: e.target.value,
                  })
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter')
                    trigger({ ...searchData, pageNumber: 1 });
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('booksManagement.books.column4')}</label>
            <div>
              <input
                type="text"
                placeholder={t('booksManagement.books.column4')}
                className="form-control"
                name="isbn"
                onChange={(e) =>
                  setSearchData({
                    ...searchData,
                    [e.target.name]: e.target.value,
                  })
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter')
                    trigger({ ...searchData, pageNumber: 1 });
                }}
                value={searchData?.isbn ?? undefined}
              />
            </div>
          </li>
          <li>
            <label>{t('booksManagement.books.column5')}</label>
            <div>
              <input
                type="text"
                placeholder={t('booksManagement.books.column5')}
                className="form-control"
                name="publisherName"
                onChange={(e) =>
                  setSearchData({
                    ...searchData,
                    [e.target.name]: e.target.value,
                  })
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter')
                    trigger({ ...searchData, pageNumber: 1 });
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('booksManagement.books.column11')}</label>
            <div>
              <SelectLevel
                value={searchData.courseCode}
                placeHolder={t('booksManagement.books.column11')}
                isAllOption={true}
                onChange={(value) =>
                  setSearchData({ ...searchData, courseCode: value })
                }
              />
            </div>
          </li>
          <li>
            <label>{t('booksManagement.books.column12')}</label>
            <div>
              <input
                type="text"
                placeholder={t('booksManagement.books.column12')}
                className="form-control"
                name="bookLevel"
                onChange={(e) =>
                  setSearchData({
                    ...searchData,
                    [e.target.name]: e.target.value,
                  })
                }
                onKeyPress={(e) => {
                  if (e.key === 'Enter')
                    trigger({ ...searchData, pageNumber: 1 });
                }}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={() => {
                  trigger({ ...searchData, pageNumber: 1 });
                  setPage(1);
                }}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <Block className="content">
      {isUploading || isQuantityUploading ? <Loader /> : null}
      <ErrorResponse error={bookError} />
      <Split
        className="splite-divide"
        sizes={[50, 50]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >
        {isBooksLoading ? <Loader /> : null}
        <ErrorResponse error={bookError} />

        <div className="col-frame left_panel">
          <div className="comm-bx__between">
            <div className="comm-sch_result">
              {t('common.text.count')}: <b>{response?.data?.totalCount ?? 0}</b>
              {t('books.text.6')} ({t('booksManagement.books.column13')}:{' '}
              <b>{response?.data?.duplicateCount ?? 0}</b>{t('books.text.7')})
            </div>
          </div>
          <div className="comm-table__area mt-0">
            <Table
              columns={columns}
              dataSource={response?.data?.books ?? []}
              sticky={true}
              rowKey={(record) => record?.bookNo}
              loading={isFetching}
              pagination={{
                current: page,
                pageSize: 50,
              }}
              onRow={(record) => {
                return {
                  onClick: () => {
                    setSelectedRow(record);
                    setIsModify(false);
                  },
                };
              }}
              rowClassName={(record: any) =>
                record.bookNo === selectedRow?.bookNo
                  ? 'chosen'
                  : 'pointer'
              }
            />
          </div>
          <div className="comm-bx__between">
            <DownloadButton onClick={onExportTable} />
            <div className="common-paging center">
              <Pagination
                count={response?.data?.totalCount ?? 0}
                page={page}
                perPage={searchData!.pageSize}
                param={searchData}
                setPage={setPage}
                small
              />
            </div>
          </div>
        </div>
        <div className="col-frame right_panel"> 
            {isHeadOffice(campusCode) && (
              <div className="comm-bx__space border-bottom-light">
                <div className='comm-btn__area right'>
                  <Button
                    className="btn-basic pink"
                    onClick={() => setIsAllFileVisible(true)}
                    text={t('booksManagement.books.button1')}
                  />
                  <Button
                    className="btn-basic pink"
                    onClick={() => setIsBookBatchQuantityRegistration(true)}
                    text={t('booksManagement.books.button2')}
                  />
                  <Button
                    className="btn-basic pink"
                    onClick={() => {
                      setSelectedRow({});
                      setIsModify(true);
                    }}
                    text={t('common.button.create')}
                  />
                </div>
              </div>
            )}
          <div>
            <div className="flex w-100">
              <div
                className={` ${
                  isHeadOffice(campusCode) ? 'col-7' : 'col-12'
                }`}
              >
                <div className="comm-bx__between">
                  <h3 className='sub-title'>{t('books.text.8')}</h3>
                </div>
                {selectedRow ? (
                  <BookBasicInformation
                    information={information}
                    setInformation={setInformation}
                    selectedRow={selectedRow}
                    isModify={isModify}
                  />
                ) : (
                  <>
                    <div className='comm-table__area'>
                      <TableEmpty text={t('shuttle.text.40')} />
                    </div>
                  </>
                )}
              </div>
              {isHeadOffice(campusCode) && (
                <div className="col-5">
                  <div className="comm-bx__between">
                    <h3 className='sub-title'>{t('books.text.9')}</h3>
                  </div>
                  {selectedRow ? (
                    <BookCopyInformation
                      selectedRow={selectedRow}
                      setSelectedRow={setSelectedRow}
                      campusBookCopyInformation={campusBookCopyInformation}
                      setCampusBookCopyInformation={
                        setCampusBookCopyInformation
                      }
                      isModify={isModify}
                      onHandleModifyButton={handleModifyForm}
                    />
                  ) : (
                    <>
                    <div className='comm-table__area'>
                      <TableEmpty text={t('shuttle.text.40')} />
                    </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      {isAllFileVisible && (
        <BookFileUploadModal
          visible={isAllFileVisible}
          onConfirm={handleExcelBatchUploadApply}
          onCancel={handleExcelBatchUploadCancel}
          file={file}
          onSetFile={setFile}
          titleText={t('booksManagement.books.button1')}
          cancelText={t('common.button.cancel')}
          confirmText={t('common.button.create')}
        />
      )}

      {isBookBatchQuantityRegistration && (
        <BookFileUploadModal
          visible={isBookBatchQuantityRegistration}
          onConfirm={handleExcelBatchQuantityUploadApply}
          onCancel={handleExcelBatchUploadCancel}
          isCampusBatchQuantity={true}
          file={file}
          onSetFile={setFile}
          titleText={t('books.text.10')}
          cancelText={t('common.button.cancel')}
          confirmText={t('books.text.11')}
        />
      )}
      </Split>
    </Block>
    </>
  );
};

export default Books;
